import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Flags = Record<string, unknown>;

/**
 * Subset of `window.App.Features` the pre-approvals eligibility check reads.
 * Declared locally (rather than via a global `Window.App` augmentation) so the
 * shared library stays self-contained and doesn't clash with the differing
 * `Window.App` declarations each consuming MFE ships in its own `global.d.ts`.
 */
export interface PreApprovalsFeatureGates {
    EnableGreenskyIntegration?: boolean;
    EnableSynchronyIntegration?: boolean;
}

/**
 * Computes the ordered list of pre-approvals providers that qualify for the
 * current tenant per the feature-gate + LD-flag rule.
 *
 * A provider qualifies when both its pre-approvals LD flag and its integration
 * Feature Gate are enabled. Shared by the prequals-settings MFE (section
 * visibility, picker options, saved-provider filter) and the providers MFE
 * (the "Pre-qualifications" / "Prescreening" tab label) so they agree on one
 * source of truth.
 */
export const getPreApprovalsEligibleProviders = (
    flags: Flags,
    features: PreApprovalsFeatureGates | undefined,
): string[] => {
    const result: string[] = [];
    if (flags.finPreApprovalsGs && features?.EnableGreenskyIntegration) {
        result.push('GreenSky');
    }
    if (flags.finPreApprovalsSynchrony && features?.EnableSynchronyIntegration) {
        result.push('Synchrony');
    }
    return result;
};

/**
 * Hook form of {@link getPreApprovalsEligibleProviders}. Wires the LD flag
 * source together with the non-reactive `window.App.Features` feature gates
 * (set by the host before MFE bootstrap and static for the page lifetime), so
 * callers can't forget to pass the feature gates.
 */
export const usePreApprovalsEligibleProviders = (): string[] => {
    const flags = useFlags();
    return useMemo(() => {
        const features = (window as { App?: { Features?: PreApprovalsFeatureGates } }).App
            ?.Features;
        return getPreApprovalsEligibleProviders(flags, features);
        // `window.App.Features` is a non-reactive global; only `flags` drives recomputation.
    }, [flags]);
};
